import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  ArrowLeft, Search, Filter, Home, Building2, Edit, Eye, 
  Image as ImageIcon, FileText, Upload, X, Plus, Check,
  Globe, RefreshCw, ChevronRight, Save, Loader2
} from 'lucide-react';
import { API } from '@/App';

export const PropertyMarketingPage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [properties, setProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [marketingData, setMarketingData] = useState(null);

  useEffect(() => {
    fetchProperties();
  }, []);

  useEffect(() => {
    filterProperties();
  }, [searchTerm, departmentFilter, statusFilter, properties]);

  const fetchProperties = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/marketing/properties`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProperties(response.data.properties || []);
    } catch (error) {
      console.error('Error fetching properties:', error);
      toast.error('Failed to load properties');
    } finally {
      setLoading(false);
    }
  };

  const filterProperties = () => {
    let filtered = [...properties];
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(p => 
        p.address?.toLowerCase().includes(term) ||
        p.postcode?.toLowerCase().includes(term) ||
        p.reference?.toLowerCase().includes(term)
      );
    }
    
    if (departmentFilter !== 'all') {
      filtered = filtered.filter(p => p.department === departmentFilter);
    }
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(p => p.status === statusFilter);
    }
    
    setFilteredProperties(filtered);
  };

  const selectProperty = async (property) => {
    setSelectedProperty(property);
    setEditMode(false);
    
    // Fetch marketing data for this property
    try {
      const response = await axios.get(`${API}/marketing/property/${property.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMarketingData(response.data);
    } catch (error) {
      // If no marketing data exists, create default
      setMarketingData({
        property_id: property.id,
        department: property.department,
        summary: '',
        full_description: '',
        features: [],
        photos: [],
        floorplan: '',
        epc_document: '',
        virtual_tour: '',
        portal_status: {
          rightmove: true,
          zoopla: true,
          onthemarket: true
        }
      });
    }
  };

  const handleSaveMarketing = async () => {
    if (!marketingData) return;
    
    setSaving(true);
    try {
      await axios.post(`${API}/marketing/property/${selectedProperty.id}`, marketingData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Marketing data saved successfully');
      setEditMode(false);
      fetchProperties(); // Refresh to show updated status
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Failed to save marketing data');
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('property_id', selectedProperty.id);
    formData.append('type', 'photo');
    
    try {
      const response = await axios.post(`${API}/marketing/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setMarketingData(prev => ({
        ...prev,
        photos: [...(prev.photos || []), response.data.url]
      }));
      toast.success('Photo uploaded');
    } catch (error) {
      toast.error('Failed to upload photo');
    }
  };

  const removePhoto = (index) => {
    setMarketingData(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
  };

  const addFeature = () => {
    setMarketingData(prev => ({
      ...prev,
      features: [...(prev.features || []), '']
    }));
  };

  const updateFeature = (index, value) => {
    const updated = [...(marketingData.features || [])];
    updated[index] = value;
    setMarketingData(prev => ({ ...prev, features: updated }));
  };

  const removeFeature = (index) => {
    setMarketingData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'for_sale': { label: 'For Sale', className: 'bg-emerald-100 text-emerald-700' },
      'available': { label: 'Available', className: 'bg-emerald-100 text-emerald-700' },
      'sstc': { label: 'SSTC', className: 'bg-amber-100 text-amber-700' },
      'under_offer': { label: 'Under Offer', className: 'bg-amber-100 text-amber-700' },
      'let_agreed': { label: 'Let Agreed', className: 'bg-amber-100 text-amber-700' },
      'let': { label: 'Let', className: 'bg-blue-100 text-blue-700' },
      'sold': { label: 'Sold', className: 'bg-slate-100 text-slate-700' },
      'withdrawn': { label: 'Withdrawn', className: 'bg-red-100 text-red-700' }
    };
    const config = statusConfig[status] || { label: status, className: 'bg-slate-100 text-slate-700' };
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-14 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-xl font-bold text-slate-800">Property Marketing</h1>
              <p className="text-sm text-slate-500">Edit listings for Rightmove, Zoopla & OnTheMarket</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => navigate('/marketing/portals')}>
              <Globe className="w-4 h-4 mr-2" />
              Portal Feeds
            </Button>
            <Button variant="outline" onClick={fetchProperties}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-6">
        <div className="grid grid-cols-12 gap-6">
          {/* Property List - Left Panel */}
          <div className="col-span-5 space-y-4">
            {/* Search & Filters */}
            <div className="bg-white rounded-xl shadow-sm p-4 space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search address, postcode, reference..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <div className="flex gap-2">
                <select
                  value={departmentFilter}
                  onChange={(e) => setDepartmentFilter(e.target.value)}
                  className="flex-1 p-2 border rounded text-sm"
                >
                  <option value="all">All Departments</option>
                  <option value="sales">Sales</option>
                  <option value="lettings">Lettings</option>
                </select>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="flex-1 p-2 border rounded text-sm"
                >
                  <option value="all">All Statuses</option>
                  <option value="for_sale">For Sale</option>
                  <option value="available">Available</option>
                  <option value="sstc">SSTC</option>
                  <option value="let_agreed">Let Agreed</option>
                </select>
              </div>
            </div>

            {/* Property List */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="p-3 border-b bg-slate-50 flex items-center justify-between">
                <span className="text-sm font-medium text-slate-600">
                  {filteredProperties.length} Properties
                </span>
                <div className="flex gap-1">
                  <Badge variant="outline" className="text-xs">
                    <Home className="w-3 h-3 mr-1" />
                    {properties.filter(p => p.department === 'sales').length} Sales
                  </Badge>
                  <Badge variant="outline" className="text-xs">
                    <Building2 className="w-3 h-3 mr-1" />
                    {properties.filter(p => p.department === 'lettings').length} Lettings
                  </Badge>
                </div>
              </div>
              <div className="max-h-[600px] overflow-y-auto divide-y">
                {filteredProperties.map((property) => (
                  <div
                    key={property.id}
                    className={`p-3 cursor-pointer hover:bg-slate-50 transition-colors ${
                      selectedProperty?.id === property.id ? 'bg-amber-50 border-l-4 border-amber-500' : ''
                    }`}
                    onClick={() => selectProperty(property)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-800 truncate">{property.address}</p>
                        <p className="text-xs text-slate-500">{property.postcode}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-xs">
                            {property.department === 'sales' ? (
                              <><Home className="w-3 h-3 mr-1" /> Sales</>
                            ) : (
                              <><Building2 className="w-3 h-3 mr-1" /> Lettings</>
                            )}
                          </Badge>
                          {getStatusBadge(property.status)}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-slate-800">
                          {property.department === 'sales' 
                            ? `£${(property.price || 0).toLocaleString()}`
                            : `£${(property.rent || 0).toLocaleString()} pcm`
                          }
                        </p>
                        <div className="flex items-center gap-1 mt-1">
                          {property.has_photos && (
                            <ImageIcon className="w-3 h-3 text-emerald-500" />
                          )}
                          {property.has_description && (
                            <FileText className="w-3 h-3 text-emerald-500" />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                {filteredProperties.length === 0 && (
                  <div className="p-8 text-center text-slate-400">
                    No properties found
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Property Details - Right Panel */}
          <div className="col-span-7">
            {selectedProperty ? (
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                {/* Property Header */}
                <div className="p-4 border-b bg-slate-50">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-lg font-bold text-slate-800">{selectedProperty.address}</h2>
                      <p className="text-sm text-slate-500">{selectedProperty.postcode} • {selectedProperty.reference}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {!editMode ? (
                        <Button onClick={() => setEditMode(true)}>
                          <Edit className="w-4 h-4 mr-2" />
                          Edit Marketing
                        </Button>
                      ) : (
                        <>
                          <Button variant="outline" onClick={() => setEditMode(false)}>
                            Cancel
                          </Button>
                          <Button onClick={handleSaveMarketing} disabled={saving}>
                            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                            Save
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Marketing Content */}
                <div className="p-4 space-y-6">
                  {/* Portal Status */}
                  <div>
                    <h3 className="text-sm font-semibold text-slate-700 mb-2">Portal Status</h3>
                    <div className="flex gap-4">
                      {['rightmove', 'zoopla', 'onthemarket'].map((portal) => (
                        <label key={portal} className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={marketingData?.portal_status?.[portal] ?? true}
                            onChange={(e) => {
                              if (editMode) {
                                setMarketingData(prev => ({
                                  ...prev,
                                  portal_status: {
                                    ...prev.portal_status,
                                    [portal]: e.target.checked
                                  }
                                }));
                              }
                            }}
                            disabled={!editMode}
                            className="rounded"
                          />
                          <span className="text-sm capitalize">{portal}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Summary */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-semibold text-slate-700">Summary (200 chars max)</h3>
                      <span className="text-xs text-slate-400">
                        {(marketingData?.summary || '').length} / 200
                      </span>
                    </div>
                    <textarea
                      value={marketingData?.summary || ''}
                      onChange={(e) => {
                        if (e.target.value.length <= 200) {
                          setMarketingData(prev => ({ ...prev, summary: e.target.value }));
                        }
                      }}
                      disabled={!editMode}
                      className="w-full p-3 border rounded min-h-[80px] text-sm disabled:bg-slate-50"
                      placeholder="Brief property summary for search results..."
                      maxLength={200}
                    />
                  </div>

                  {/* Full Description */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-semibold text-slate-700">Full Description</h3>
                      <span className="text-xs text-slate-400">
                        {(marketingData?.full_description || '').length} chars
                      </span>
                    </div>
                    <textarea
                      value={marketingData?.full_description || ''}
                      onChange={(e) => setMarketingData(prev => ({ ...prev, full_description: e.target.value }))}
                      disabled={!editMode}
                      className="w-full p-3 border rounded min-h-[150px] text-sm disabled:bg-slate-50"
                      placeholder="Detailed property description..."
                    />
                  </div>

                  {/* Key Features */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-semibold text-slate-700">Key Features</h3>
                      {editMode && (
                        <Button variant="outline" size="sm" onClick={addFeature}>
                          <Plus className="w-3 h-3 mr-1" /> Add
                        </Button>
                      )}
                    </div>
                    <div className="space-y-2">
                      {(marketingData?.features || []).map((feature, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <span className="text-slate-400">•</span>
                          <Input
                            value={feature}
                            onChange={(e) => updateFeature(i, e.target.value)}
                            disabled={!editMode}
                            className="flex-1 text-sm"
                            placeholder={`Feature ${i + 1}`}
                          />
                          {editMode && (
                            <Button variant="ghost" size="sm" onClick={() => removeFeature(i)}>
                              <X className="w-4 h-4 text-slate-400" />
                            </Button>
                          )}
                        </div>
                      ))}
                      {(marketingData?.features || []).length === 0 && !editMode && (
                        <p className="text-sm text-slate-400 italic">No features added</p>
                      )}
                    </div>
                  </div>

                  {/* Photos */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-semibold text-slate-700">Photos</h3>
                      {editMode && (
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={handlePhotoUpload}
                          />
                          <Button variant="outline" size="sm" asChild>
                            <span><Upload className="w-3 h-3 mr-1" /> Upload</span>
                          </Button>
                        </label>
                      )}
                    </div>
                    <div className="grid grid-cols-4 gap-2">
                      {(marketingData?.photos || []).map((photo, i) => (
                        <div key={i} className="relative aspect-square rounded overflow-hidden bg-slate-100">
                          <img src={photo} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" />
                          {editMode && (
                            <button
                              onClick={() => removePhoto(i)}
                              className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs"
                            >
                              ×
                            </button>
                          )}
                          <span className="absolute bottom-1 left-1 bg-black/50 text-white text-xs px-1 rounded">
                            {i + 1}
                          </span>
                        </div>
                      ))}
                      {(marketingData?.photos || []).length === 0 && (
                        <div className="col-span-4 p-8 text-center text-slate-400 border-2 border-dashed rounded-lg">
                          <ImageIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
                          <p className="text-sm">No photos uploaded</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Floorplan & Virtual Tour */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <h3 className="text-sm font-semibold text-slate-700 mb-2">Floorplan URL</h3>
                      <Input
                        value={marketingData?.floorplan || ''}
                        onChange={(e) => setMarketingData(prev => ({ ...prev, floorplan: e.target.value }))}
                        disabled={!editMode}
                        placeholder="https://..."
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-slate-700 mb-2">Virtual Tour URL</h3>
                      <Input
                        value={marketingData?.virtual_tour || ''}
                        onChange={(e) => setMarketingData(prev => ({ ...prev, virtual_tour: e.target.value }))}
                        disabled={!editMode}
                        placeholder="https://..."
                        className="text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm p-12 text-center">
                <Home className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-600 mb-2">Select a Property</h3>
                <p className="text-sm text-slate-400">
                  Choose a property from the list to view and edit its marketing details
                </p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default PropertyMarketingPage;
